<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CampaignsAPI from 'dashboard/api/campaigns';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { useAlert } from 'dashboard/composables';

const route = useRoute();
const router = useRouter();
const ui = {
  back: 'Voltar',
  title: 'Detalhes da campanha',
  contact: 'Contato',
  status: 'Status',
  acceptedSent: 'Aceita / Enviada',
  deliveredRead: 'Entregue / Lida',
  wamid: 'WAMID',
  metaError: 'Erro da Meta',
  conversation: 'Conversa',
  openConversation: 'Abrir conversa',
  noResults: 'Nenhuma entrega encontrada.',
  emptyValue: '-',
  search: 'Buscar por contato, telefone, WAMID ou erro',
};
const loading = ref(true);
const campaign = ref(null);
const summary = ref({});
const deliveries = ref([]);
const meta = ref({ current_page: 1, per_page: 25, total: 0 });
const search = ref('');
const status = ref('');
let searchTimer;

const statusOptions = [
  ['', 'Todos os status'],
  ['accepted', 'Aceita'],
  ['sent', 'Enviada'],
  ['delivered', 'Entregue'],
  ['read', 'Lida'],
  ['failed', 'Falha'],
  ['pending', 'Pendente'],
];

const statusLabels = Object.fromEntries(statusOptions);
const statusClasses = {
  accepted: 'bg-n-blue-3 text-n-blue-11',
  sent: 'bg-n-cyan-3 text-n-cyan-11',
  delivered: 'bg-n-teal-3 text-n-teal-11',
  read: 'bg-n-violet-3 text-n-violet-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  pending: 'bg-n-slate-3 text-n-slate-11',
};

const cards = computed(() => [
  ['Total', summary.value.total || 0, 'text-n-slate-12'],
  ['Aceitas', summary.value.accepted || 0, 'text-n-blue-11'],
  ['Enviadas', summary.value.sent || 0, 'text-n-cyan-11'],
  ['Entregues', summary.value.delivered || 0, 'text-n-teal-11'],
  ['Lidas', summary.value.read || 0, 'text-n-violet-11'],
  ['Falhas', summary.value.failed || 0, 'text-n-ruby-11'],
]);

const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(value));
};

const fetchDeliveries = async (page = meta.value.current_page) => {
  loading.value = true;
  try {
    const { data } = await CampaignsAPI.getDeliveries(route.params.campaignId, {
      page,
      per_page: meta.value.per_page,
      q: search.value || undefined,
      status: status.value || undefined,
    });
    campaign.value = data.campaign;
    summary.value = data.summary;
    deliveries.value = data.deliveries;
    meta.value = data.meta;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Nao foi possivel carregar as entregas.'
    );
  } finally {
    loading.value = false;
  }
};

const goBack = () => router.push({ name: 'campaigns_whatsapp_index' });
const goToConversation = conversationId => {
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
};

watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => fetchDeliveries(1), 350);
});
watch(status, () => fetchDeliveries(1));
onMounted(() => fetchDeliveries(1));
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="px-6 border-b border-n-weak">
      <div class="flex items-center gap-3 h-20 w-full max-w-7xl mx-auto">
        <Button
          icon="i-lucide-arrow-left"
          variant="ghost"
          color="slate"
          :title="ui.back"
          @click="goBack"
        />
        <div class="min-w-0">
          <h1 class="text-heading-1 text-n-slate-12 truncate">
            {{ campaign?.title || ui.title }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ campaign?.inbox?.name }}
          </p>
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6 py-5">
      <div class="w-full max-w-7xl mx-auto space-y-5">
        <div
          class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 border border-n-weak rounded-lg overflow-hidden"
        >
          <div
            v-for="([label, value, color], index) in cards"
            :key="label"
            class="px-4 py-3 bg-n-surface-2"
            :class="{ 'border-l border-n-weak': index }"
          >
            <p class="text-xs text-n-slate-11">{{ label }}</p>
            <p class="text-xl font-semibold tabular-nums" :class="color">
              {{ value }}
            </p>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-3">
          <Input v-model="search" :placeholder="ui.search" class="flex-1" />
          <select
            v-model="status"
            class="h-10 px-3 rounded-md bg-n-alpha-black2 outline outline-1 outline-n-weak text-sm text-n-slate-12"
          >
            <option
              v-for="option in statusOptions"
              :key="option[0]"
              :value="option[0]"
            >
              {{ option[1] }}
            </option>
          </select>
          <Button
            icon="i-lucide-refresh-cw"
            variant="faded"
            color="slate"
            label="Atualizar"
            @click="fetchDeliveries()"
          />
        </div>

        <div
          class="border border-n-weak rounded-lg overflow-x-auto bg-n-surface-2"
        >
          <div v-if="loading" class="flex justify-center py-14">
            <Spinner />
          </div>
          <table v-else class="w-full min-w-[1080px] text-sm">
            <thead class="bg-n-alpha-2 text-left text-xs text-n-slate-11">
              <tr>
                <th class="px-4 py-3">{{ ui.contact }}</th>
                <th class="px-4 py-3">{{ ui.status }}</th>
                <th class="px-4 py-3">{{ ui.acceptedSent }}</th>
                <th class="px-4 py-3">{{ ui.deliveredRead }}</th>
                <th class="px-4 py-3">{{ ui.wamid }}</th>
                <th class="px-4 py-3">{{ ui.metaError }}</th>
                <th class="px-4 py-3 text-right">{{ ui.conversation }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="delivery in deliveries"
                :key="delivery.id"
                class="hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3">
                  <p class="font-medium text-n-slate-12">
                    {{ delivery.contact?.name || 'Sem nome' }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{ delivery.recipient_phone }}
                  </p>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex px-2 py-1 rounded-md text-xs font-medium"
                    :class="statusClasses[delivery.status]"
                  >
                    {{ statusLabels[delivery.status] || delivery.status }}
                  </span>
                </td>
                <td class="px-4 py-3 text-xs text-n-slate-11">
                  <p>{{ formatDate(delivery.accepted_at) }}</p>
                  <p>{{ formatDate(delivery.sent_at) }}</p>
                </td>
                <td class="px-4 py-3 text-xs text-n-slate-11">
                  <p>{{ formatDate(delivery.delivered_at) }}</p>
                  <p>{{ formatDate(delivery.read_at) }}</p>
                </td>
                <td class="px-4 py-3 max-w-48">
                  <code class="text-xs break-all text-n-slate-11">{{
                    delivery.wamid || '-'
                  }}</code>
                </td>
                <td class="px-4 py-3 max-w-72">
                  <p v-if="delivery.error" class="text-xs text-n-ruby-11">
                    <strong>{{ delivery.error.code }}</strong>
                    {{ delivery.error.message }}
                  </p>
                  <span v-else class="text-n-slate-10">{{
                    ui.emptyValue
                  }}</span>
                </td>
                <td class="px-4 py-3 text-right">
                  <Button
                    v-if="delivery.conversation_id"
                    icon="i-lucide-message-circle"
                    variant="ghost"
                    color="slate"
                    size="sm"
                    :title="ui.openConversation"
                    @click="goToConversation(delivery.conversation_id)"
                  />
                </td>
              </tr>
              <tr v-if="!deliveries.length">
                <td colspan="7" class="px-4 py-12 text-center text-n-slate-11">
                  {{ ui.noResults }}
                </td>
              </tr>
            </tbody>
          </table>
          <PaginationFooter
            v-if="meta.total > meta.per_page"
            :current-page="meta.current_page"
            :total-items="meta.total"
            :items-per-page="meta.per_page"
            @update:current-page="fetchDeliveries"
          />
        </div>
      </div>
    </main>
  </section>
</template>
